import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, ArrowLeft } from "lucide-react";
import { useLang } from "../../i18n";
import { getPaymentStatus } from "../../lib/api";

const THEMES = {
  "fc-inkad": {
    wrap: "fc-theme", card: "border border-white/10 bg-[#141414] rounded-sm",
    title: "font-anton uppercase text-white", accent: "text-[#CCFF00]", spinner: "text-[#CCFF00]",
    back: "/fc-inkad", retryBtn: "rounded-sm bg-[#CCFF00] text-black font-extrabold uppercase",
    backText: "text-white/50 hover:text-[#CCFF00]",
  },
  jeugd: {
    wrap: "jeugd-theme", card: "bg-white shadow-[0_8px_30px_rgba(0,0,0,0.05)] rounded-[2rem]",
    title: "font-fredoka font-bold text-[#262626]", accent: "text-[#81B29A]", spinner: "text-[#E07A5F]",
    back: "/jeugd", retryBtn: "rounded-full bg-[#E07A5F] text-white font-bold",
    backText: "text-[#52525B] hover:text-[#E07A5F]",
  },
};

export default function PaymentReturn({ branch = "fc-inkad" }) {
  const { t } = useLang();
  const [params] = useSearchParams();
  const rid = params.get("registration_id");
  const [state, setState] = useState({ loading: true });
  const th = THEMES[branch] || THEMES["fc-inkad"];
  const backHome = branch === "jeugd" ? t("backToYouth") : t("backToClub");

  useEffect(() => {
    if (!rid) { setState({ loading: false, error: true }); return; }
    let stopped = false;
    let attempts = 0;
    const check = async () => {
      try {
        const data = await getPaymentStatus(rid);
        if (stopped) return;
        if (data.paid || ["failed", "canceled", "expired"].includes(data.status) || attempts >= 8) {
          setState({ loading: false, ...data });
        } else {
          attempts += 1;
          setTimeout(check, 2500);
        }
      } catch {
        if (!stopped) setState({ loading: false, error: true });
      }
    };
    check();
    return () => { stopped = true; };
  }, [rid]);

  const paid = state.paid;
  const failed = state.error || ["failed", "canceled", "expired"].includes(state.status);
  const registerPath = branch === "jeugd" ? "/jeugd/inschrijven" : "/fc-inkad/inschrijven";

  return (
    <div className={`${th.wrap} min-h-screen flex items-center justify-center px-6 py-24`} data-testid="payment-return">
      <div className={`w-full max-w-lg p-10 text-center ${th.card}`}>
        {state.loading ? (
          <>
            <Loader2 size={48} className={`mx-auto mb-5 animate-spin ${th.spinner}`} />
            <p className={`text-2xl ${th.title}`} data-testid="pay-status-loading">{t("payConfirming")}</p>
          </>
        ) : paid ? (
          <>
            <CheckCircle2 size={56} className={`mx-auto mb-5 ${th.accent}`} />
            <p className={`text-3xl ${th.title}`} data-testid="pay-status-paid">{t("payPaid")}</p>
          </>
        ) : failed ? (
          <>
            <XCircle size={56} className="mx-auto mb-5 text-red-500" />
            <p className={`text-2xl ${th.title}`} data-testid="pay-status-failed">{t("payFailed")}</p>
            <Link to={registerPath} className={`mt-6 inline-block px-6 py-3 text-sm ${th.retryBtn}`} data-testid="pay-retry">
              {t("payRetry")}
            </Link>
          </>
        ) : (
          <>
            <Loader2 size={48} className="mx-auto mb-5 text-black/30" />
            <p className={`text-2xl ${th.title}`} data-testid="pay-status-open">{t("payOpen")}</p>
          </>
        )}
        <Link to={th.back} className={`mt-8 inline-flex items-center gap-2 text-sm transition-colors ${th.backText}`} data-testid="pay-back">
          <ArrowLeft size={15} /> {backHome}
        </Link>
      </div>
    </div>
  );
}
